import React, { forwardRef, useState, useEffect } from "react";
import { UsdtToken, tba } from "../../../assets";
import XBRImage from "../../../assets/icons/XBRimage.png";
import ellipse from "../../../assets/ellipse-nft.webp";
import InboxSvg from "../../../components/svgComponents/InboxSvg";
import { getLiquidity } from "../../../uniswap/liquidity";
import { getPositionIds } from "../../../uniswap/position";
import MyNftsHeader from "../MyNftsHeader/MyNftsHeader";
import Nft from "../Nft/Nft";
import { Loader } from "../../../components/Loader";
import { useContextProvider } from '../../../context';

const MyNFTs = forwardRef((props, ref) => {
	const {walletAddress, jsonRpcProvider}= useContextProvider();
  const [positionIds, setPositionIds] = useState([]);
  const [liquidityData, setLiquidityData] = useState({});
  const [isLoadingNfts, setIsLoadingNfts] = useState(false);

  const tokenImageMap = {
    XBR: XBRImage,
    USDT: UsdtToken,
    TBA: tba,
  };

  useEffect(() => {
    const fetchPositionIds = async () => {
      setIsLoadingNfts(true);

      if (!walletAddress) {
        setPositionIds([]);
        setIsLoadingNfts(false);
        return;
      }

      const cachedPositionIds = localStorage.getItem("positionIds");

      if (
        cachedPositionIds &&
        cachedPositionIds !== "undefined" &&
        cachedPositionIds !== "null"
      ) {
        setPositionIds(JSON.parse(cachedPositionIds));

        setTimeout(() => {
          setIsLoadingNfts(false);
        }, 2500);
      }

      try {
        const positions = await getPositionIds(walletAddress, jsonRpcProvider);

        if (positions) {
          setPositionIds(positions);
          localStorage.setItem("positionIds", JSON.stringify(positions));
        }
      } catch (error) {
        console.error("Error fetching positionIds:", error);
      } finally {
        setTimeout(() => {
          setIsLoadingNfts(false);
        }, 2500);
      }
    };
    fetchPositionIds(walletAddress, jsonRpcProvider);
  }, [walletAddress, jsonRpcProvider]);

  const formatBalanceForPrice = (balance) => {
    const epsilon = 1e-6;
    const infinityThreshold = 1e20;

    if (balance < epsilon) {
      return "0";
    }
    if (balance > infinityThreshold) {
      return "∞";
    }
    const num = parseFloat(balance);
    if (num >= 1e12) {
      return (num / 1e12).toFixed(1).replace(/\.0$/, "") + "T";
    } else if (num >= 1e9) {
      return (num / 1e9).toFixed(1).replace(/\.0$/, "") + "B";
    } else if (num >= 1e6) {
      return (num / 1e6).toFixed(1).replace(/\.0$/, "") + "M";
    } else if (num >= 1e3) {
      return (num / 1e3).toFixed(1).replace(/\.0$/, "") + "K";
    } else {
      return num.toFixed(4).replace(/\.?0+$/, "");
    }
  };

  const getLiquidityData = async (option) => {
    const { token0, token1, fee, tickLower, tickUpper } =
      await getLiquidity(Number(option), jsonRpcProvider);
    const token0LiquidityAmount = token0.amount;
    const token1LiquidityAmount = token1.amount;

    const token0Name = token0.symbol;
    const token1Name = token1.symbol;

    const currentfee = fee / 10000;

    const tickToPrice = (tick) => {
      return Math.pow(1.0001, tick);
    };

    const decimalAdjustmentFactor = Math.pow(
      10,
      token1.decimals - token0.decimals
    );

    const minPrice = tickToPrice(tickLower) / decimalAdjustmentFactor;
    const maxPrice = tickToPrice(tickUpper) / decimalAdjustmentFactor;
    const minPrice1 = 1 / maxPrice;
    const maxPrice1 = 1 / minPrice;
    setLiquidityData((prevData) => ({
      ...prevData,
      [option]: {
        token0LiquidityAmount,
        token1LiquidityAmount,
        token0Name,
        token1Name,
        currentfee,
        minPrice,
        maxPrice,
        minPrice1,
        maxPrice1,
      },
    }));
  };

  useEffect(() => {
    if (!positionIds?.length) return;
    positionIds.forEach(async (option) => {
      await getLiquidityData(option);
    });
  }, [positionIds]);

  const handleAddLiquidityClick = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <div
      ref={ref}
      className={
        "w-full  bg-white dark:bg-darkCardBg relative rounded-3xl overflow-hidden"
      }
    >
      <MyNftsHeader
        positionIdsQuantity={positionIds ? `${positionIds.length}` : 0}
        handleAddLiquidityClick={handleAddLiquidityClick}
      />
      <div className="w-full relative">
        {isLoadingNfts ? (
          <div className="relative flex justify-center items-center pb-22 pt-21">
            <img
              src={ellipse}
              alt="Background decoration"
              className="absolute -top-3 md:-top-25 left-0 right-0 mx-auto"
            />
            <div className="flex flex-col items-center gap-5">
              <Loader />
            </div>
          </div>
        ) : positionIds?.length > 0 &&
          Object.keys(liquidityData)?.length > 0 ? (
          positionIds.map((option, index) => (
            <Nft
              id={option}
              key={index}
              tokenOneImageName={
                tokenImageMap[liquidityData[option]?.token0Name]
              }
              tokenTwoImageName={
                tokenImageMap[liquidityData[option]?.token1Name]
              }
              tokenOneName={liquidityData[option]?.token0Name}
              tokenTwoName={liquidityData[option]?.token1Name}
              currentFee={liquidityData[option]?.currentfee}
              tokenOneLiquidityAmount={
                liquidityData[option]?.token0LiquidityAmount
              }
              tokenTwoLiquidityAmount={
                liquidityData[option]?.token1LiquidityAmount
              }
              formatBalanceForPrice={formatBalanceForPrice}
              liquidityMinPrice={liquidityData[option]?.minPrice1}
              liquidityMaxPrice={liquidityData[option]?.maxPrice1}
            />
          ))
        ) : (
          <div className="relative flex justify-center items-center pb-22 pt-21">
            <img
              src={ellipse}
              alt="Background decoration"
              className="absolute -top-3 md:-top-25 left-0 right-0 mx-auto"
            />
            <div className="flex flex-col items-center gap-5">
              <InboxSvg />
              <p className="text-black text-sm leading-6 dark:text-white">
                Your active NFTs will appear here.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});
export default MyNFTs;
